import { collectAllDirectoryEntries, readEntriesPromise } from "@shared/dom";
import { fmtSize as fmtSizeShort, len as lenOf, throwIf } from "@shared/util";

export { fnNoOp, inflect, sleep, startTimer, throwIf } from "@shared/util";
export { collectAllDirectoryEntries, readEntriesPromise };

export function len(o: any[] | string | FormData | null | undefined) {
  if (!o) {
    return 0;
  }
  if (o instanceof FormData) {
    // TODO: or fix callers to not use on FormData
    return Array.from(o.keys()).length;
  }
  return lenOf(o);
}

// https://github.dev/dropbox/dropbox-sdk-js/blob/main/examples/javascript/utils.js#L6
export function parseQueryString(str: string) {
  const ret = Object.create(null);

  if (typeof str !== "string") {
    return ret;
  }

  str = str.trim().replace(/^(\?|#|&)/, "");

  if (!str) {
    return ret;
  }

  str.split("&").forEach((param) => {
    const parts = param.replace(/\+/g, " ").split("=");
    // Firefox (pre 40) decodes `%3D` to `=`
    // https://github.com/sindresorhus/query-string/pull/37
    let key = parts.shift();
    let val = parts.length > 0 ? parts.join("=") : undefined;

    key = decodeURIComponent(key);

    // missing `=` should be `null`:
    // http://w3.org/TR/2012/WD-url-20120524/#collect-url-parameters
    val = val === undefined ? null : decodeURIComponent(val);

    if (ret[key] === undefined) {
      ret[key] = val;
    } else if (Array.isArray(ret[key])) {
      ret[key].push(val);
    } else {
      ret[key] = [ret[key], val];
    }
  });

  return ret;
}

export function openBrowserWindow(url: string | URL | undefined, title: string | undefined, w: number, h: number) {
  const screenLeft = window.screenLeft;
  const screenTop = window.screenTop;

  const width = window.innerWidth
    ? window.innerWidth
    : document.documentElement.clientWidth
      ? document.documentElement.clientWidth
      : screen.width;
  const height = window.innerHeight
    ? window.innerHeight
    : document.documentElement.clientHeight
      ? document.documentElement.clientHeight
      : screen.height;

  const left = width / 2 - w / 2 + screenLeft;
  const top = height / 2 - h / 2 + screenTop;
  const newWindow = window.open(
    url,
    title,
    "scrollbars=yes, width=" + w + ", height=" + h + ", top=" + top + ", left=" + left,
  );

  // Puts focus on the newWindow
  if (newWindow) {
    newWindow.focus();
  }
}

export function fmtSizeFull(n: number): string {
  if (!n && n !== 0) {
    return "-";
  }
  let s = fmtSize(n);
  return s + ` (${n.toLocaleString("en-US")} bytes)`;
}

export function fmtSize(n: number): string {
  if (typeof n == "undefined") {
    // google drive has some entries without size
    return "-";
  }
  if (n < 0) {
    // for directories
    return "";
  }
  return fmtSizeShort(n);
}

export function timeSince(d: Date): number {
  const now = new Date().getTime();
  return now - d.getTime();
}

export function trimSuffix(s: string, prefix: string): string {
  if (s.startsWith(prefix)) {
    return s.substring(len(prefix));
  }
  return s;
}

export function copyTextToClipboard(s: string) {
  if (navigator.clipboard) {
    navigator.clipboard.writeText(s).then(ok, err);
    function ok() {
      console.log(`copied to clipboard: '${s}'`);
    }
    function err(e: any) {
      console.error("failed to copy to clipboard", e);
    }
  }
}

// https://gist.github.com/nmsdvid/8807205
// Returns a function, that, as long as it continues to be invoked, will not
// be triggered. The function will be called after it stops being called for
// N milliseconds. If `immediate` is passed, trigger the function on the
// leading edge, instead of the trailing.
export function debounceEvent(callback: Function, time: number) {
  let timerId: any;
  return (...args: any[]) => {
    clearTimeout(timerId);
    timerId = setTimeout(() => {
      timerId = null;
      callback(...args);
    }, time);
  };
}

// DataTransfer.items
export async function getAllFileEntries(dataTransferItemList: DataTransferItemList) {
  let fileEntries: FileSystemEntry[] = [];
  let queue: FileSystemEntry[] = [];
  let n = dataTransferItemList.length;
  for (let i = 0; i < n; i++) {
    let item = dataTransferItemList[i];
    let entry = item.webkitGetAsEntry();
    if (entry) {
      queue.push(entry);
    }
  }
  while (queue.length > 0) {
    let entry = queue.shift() as FileSystemEntry;
    if (entry.isFile) {
      fileEntries.push(entry);
    } else if (entry.isDirectory) {
      let reader = (entry as FileSystemDirectoryEntry).createReader();
      await collectAllDirectoryEntries(reader, queue);
    }
  }
  return fileEntries;
}

export function dx(left: number, right: number) {
  return right - left;
}

export function half(n: number) {
  return n / 2;
}

export function mid(left: number, right: number) {
  return left - dx(left, right) / 2;
}

export function patchPrototype(o: object, proto: object): object {
  // https://stackoverflow.com/questions/5873624/parse-json-string-into-a-particular-object-prototype-in-javascript
  Object.setPrototypeOf(o, proto);
  return o;
}

export function truncatedText(s: string, max: number = 128): string {
  if (len(s) > max) {
    return s.substring(0, max);
  }
  return s;
}

function throwOnXhrError(uri: string, xhr: XMLHttpRequest) {
  if (xhr.status >= 200 && xhr.status < 300) {
    return;
  }
  throw new Error(`${uri} failed with ${xhr.status} ${xhr.statusText}`);
}

export function putWithXhr(uri: string, file: File) {
  let xhr = new XMLHttpRequest();
  xhr.open("PUT", uri, true);
  // TODO: set Content-Type
  // xhr.setRequestHeader("Content-Length", size);
  xhr.onload = () => {
    throwOnXhrError(uri, xhr);
  };
  xhr.onerror = () => {
    throwOnXhrError(uri, xhr);
  };
  xhr.send(file);
}

export function lazyLoadScript(src: string, opts = {}): Promise<any> {
  throwIf(!src, "src parameter must be specified");
  return new Promise(function (resolve, reject) {
    const defaults = {
      force: false,
      async: true,
    };

    if (typeof opts === "string") {
      opts = {
        id: opts,
      };
    }

    opts = Object.assign({}, defaults, opts);
    const script = document.createElement("script");

    const id = opts.id;
    script.src = src;
    if (id) {
      script.setAttribute("id", id);
      if (document.getElementById(id)) {
        // console.log(`already loaded ${src}`);
        resolve(document.getElementById(id));
        return;
      }
    } else {
      const sc = document.querySelector(`script[src="${src}"]`);
      if (!opts.force && sc) {
        // console.log(`already loaded ${src}`);
        resolve(sc);
        return;
      }
    }

    if (opts.async) script.setAttribute("async", "true");
    if (opts.defer) script.setAttribute("defer", "true");
    if (opts.integrity) script.setAttribute("integrity", opts.integrity);
    if (opts.type) script.setAttribute("type", opts.type);
    if (opts.text) script.setAttribute("text", opts.text);
    if (opts.charset) script.setAttribute("charset", opts.charset);
    if (opts.crossorigin) script.setAttribute("crossorigin", opts.crossorigin);

    script.onload = function (event) {
      // console.log(`loaded ${src}`);
      resolve(script);
    };
    script.onerror = function (event) {
      reject(event);
    };
    document.body.appendChild(script);
  });
}

export function dateStringToMs(dt: Date): number {
  if (!dt) {
    // maybe return now?
    return 0;
  }
  const d = new Date(dt);
  return d.getTime();
}

export function isDev(): boolean {
  return window.location.hostname == "localhost";
}

export async function loadScript(
  src: string,
  async: boolean = true,
  type: string = "text/javascript",
): Promise<object> {
  return new Promise((resolve, reject) => {
    try {
      const el = document.createElement("script");
      const container = document.head || document.body;

      el.type = type;
      el.async = async;
      el.src = src;

      el.addEventListener("load", () => {
        resolve({ status: true });
      });

      el.addEventListener("error", () => {
        reject({
          status: false,
          message: `Failed to load the script ${src}`,
        });
      });

      container.appendChild(el);
    } catch (err) {
      reject(err);
    }
  });
}

function numEncode64(n: number): string {
  n &= 63;
  switch (true) {
    case n < 36:
      return n.toString(36);
    case n < 62:
      return (n - 26).toString(36).toUpperCase();
    case n > 62:
      return "-";
    default:
      return "_";
  }
}

export function nanoid(n: number = 20): string {
  let a = crypto.getRandomValues(new Uint8Array(n));
  return a.reduce((t, e) => t + numEncode64(e), "");
}

export function isString(v: any): boolean {
  return typeof v === "string" || v instanceof String;
}

function pad(s: string, n: number, padding: string = " "): string {
  let toAdd = n - len(s);
  while (toAdd > 0) {
    s = padding + s;
    toAdd--;
  }
  return s;
}

// How long ago a time was, short, as Sumatra File Manager shows it: "3.5 h",
// "2.0 d", "1.4 w", "5.1 m", "2.3 y". A time in the future is "0.0 h".
export function fmtAge(timeMs: number, nowMs: number): string {
  if (timeMs == 0) {
    return "";
  }
  let days = timeMs < nowMs ? (nowMs - timeMs) / (24 * 60 * 60 * 1000) : 0;
  let value = days;
  let suffix = "d";
  if (days < 1) {
    value = days * 24;
    suffix = "h";
  } else if (days >= 365) {
    value = days / 365;
    suffix = "y";
  } else if (days >= 30) {
    value = days / 30;
    suffix = "m";
  } else if (days >= 7) {
    value = days / 7;
    suffix = "w";
  }
  return value.toFixed(1) + " " + suffix;
}

export function fmtTimeMs(timeMs: number): string {
  if (timeMs == 0) {
    return "";
  }
  let d = new Date();
  d.setTime(timeMs);
  // let s = d.toDateString();
  let m = d.getMonth() + 1;
  let day = d.getDate();
  let y = d.getFullYear();
  let h = d.getHours();
  let tz = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  let hStr = pad(`${h}`, 2, " ");
  let min = d.getMinutes();
  let minStr = pad(`${min}`, 2, "0");
  let s = `${m}/${day}/${y} ${hStr}:${minStr} ${tz}`;
  return s;
}

/**
 * if key is "|foo.bar", we return o["foo"]["bar"]
 * otherwise it's just o[key]
 */
export function getObjectValue(o: any, key: string): any {
  if (!key.startsWith("|")) {
    return o[key];
  }
  key = key.substring(1);
  let keys = key.split(".");
  throwIf(len(keys) < 2, `${key} should be multi-level`);
  for (let k of keys) {
    o = o[k];
    if (!o) {
      return null;
    }
  }
  return o;
}

// https://svelte.dev/examples/modal
export function trapFocus(parent: HTMLElement, e: KeyboardEvent) {
  const nodes = parent.querySelectorAll("*:not([disabled])");
  const tabbable = Array.from(nodes).filter((n) => n.tabIndex >= 0);

  let index = tabbable.indexOf(document.activeElement);
  if (index === -1 && e.shiftKey) index = 0;

  index += tabbable.length + (e.shiftKey ? -1 : 1);
  index %= tabbable.length;

  tabbable[index].focus();
}

// Returns text made safe to put in HTML, like a file name in a label.
export function escHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
